import { apiClient } from './client';
import type { NotificationItem, RawNotification } from '../notifications/types';

/**
 * Notification REST client — mirrors the web app's notificationThunk.ts.
 * Endpoints (backend wraps payloads in `{ data: {...} }`, like the rest of the
 * add-in's API):
 *   GET  /api/v1/notification?offset&limit      → { notifications[], total_count }
 *   PUT  /api/v1/notification/mark-as-read       body { notification_id, is_read }
 *   PUT  /api/v1/notification/mark-all-as-read
 * No delete/dismiss endpoint exists.
 */

/** Page size — matches the web app's LIST_LIMIT. */
export const NOTIFICATION_PAGE_SIZE = 24;

function normalize(raw: RawNotification, idx: number): NotificationItem {
  return {
    id: raw.notification_id ?? `notif-${idx}-${Date.now()}`,
    title: raw.title ?? '',
    description: raw.message ?? '',
    createdAt: raw.created_at ?? new Date().toISOString(),
    read: raw.is_read ?? false,
    feature: raw.feature,
    featureId: raw.feature_id,
    icon: raw.icon,
  };
}

export interface NotificationPage {
  items: NotificationItem[];
  totalCount: number;
}

export async function fetchNotifications(
  offset: number,
  limit: number = NOTIFICATION_PAGE_SIZE,
): Promise<NotificationPage> {
  const res = await apiClient.get('/api/v1/notification', { params: { offset, limit } });
  const body = res.data as
    | { data?: { notifications?: RawNotification[]; total_count?: number } }
    | undefined;
  const raw = body?.data?.notifications ?? [];
  return {
    items: raw.map(normalize),
    totalCount: body?.data?.total_count ?? raw.length,
  };
}

export async function markNotificationRead(id: string, isRead = true): Promise<void> {
  await apiClient.put('/api/v1/notification/mark-as-read', {
    notification_id: id,
    is_read: isRead,
  });
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiClient.put('/api/v1/notification/mark-all-as-read');
}
